/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from 'react';
import {
    Card, Button, Select, DatePicker, message, Tag, Progress,
    Row, Col, Radio, Checkbox, Alert, Divider, Tabs, Spin, Table, Modal, Tooltip
} from 'antd';
import {
    SaveOutlined, SendOutlined, EyeOutlined, PlusOutlined,
    FileExcelOutlined, UploadOutlined, LoadingOutlined
} from '@ant-design/icons';
import QoCService from '../../services/QoCService';
import FacilityService from '../../services/FacilityService';
import util from '../../utils/util';
import moment from 'moment';
import { exportIpcafAssessmentExcel, importIpcafAssessmentExcel } from '../../utils/qocIpcafAssessmentExcel';

const { TabPane } = Tabs;

// ─── Constants ────────────────────────────────────────────────────────────────
const SECTION_COLORS = {
    CC1:'#0ea5e9', CC2:'#8b5cf6', CC3:'#f59e0b', CC4:'#10b981',
    CC5:'#ef4444', CC6:'#3b82f6', CC7:'#6366f1', CC8:'#ec4899',
};

const LEVEL_CFG = {
    Inadequate:   { color:'#f5222d', min:0,   max:200 },
    Basic:        { color:'#fa8c16', min:201, max:400 },
    Intermediate: { color:'#1890ff', min:401, max:600 },
    Advanced:     { color:'#389e0d', min:601, max:800 },
};
const levelFor = (total) => {
    if (total <= 200) return 'Inadequate';
    if (total <= 400) return 'Basic';
    if (total <= 600) return 'Intermediate';
    return 'Advanced';
};

// ─── Single question renderer ──────────────────────────────────────────────────
function QuestionBlock({ question, response, onChange, disabled }) {
    const selected = response?.selected_options || [];
    const score = response?.score || 0;

    const handleSingleChange = (e) => {
        const key = e.target.value;
        const opt = question.options.find(o => o.key === key);
        onChange(question.id, [key], opt ? opt.score : 0);
    };

    const handleMultiChange = (checkedKeys) => {
        const total = question.options
            .filter(o => checkedKeys.includes(o.key))
            .reduce((sum, o) => sum + Number(o.score), 0);
        onChange(question.id, checkedKeys, total);
    };

    return (
        <div style={{
            padding:'14px 16px', marginBottom:10, borderRadius:8,
            border: score > 0 ? '1px solid #d9f7be' : '1px solid #f0f0f0',
            background: score > 0 ? '#fafffa' : '#fff',
        }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:8 }}>
                <div style={{ flex:1 }}>
                    <span style={{ fontFamily:'monospace', fontSize:11, color:'#888', marginRight:8 }}>
                        {question.ref_no}
                    </span>
                    <span style={{ fontSize:13, fontWeight:500 }}>{question.question_text}</span>
                </div>
                <Tag color={score > 0 ? 'success' : 'default'} style={{ marginLeft:12, flexShrink:0 }}>
                    {score} pts
                </Tag>
            </div>

            {question.question_type === 'single' ? (
                <Radio.Group
                    value={selected[0]}
                    onChange={handleSingleChange}
                    disabled={disabled}
                    style={{ display:'flex', flexDirection:'column', gap:6, marginLeft:8 }}>
                    {question.options.map(opt => (
                        <Radio key={opt.key} value={opt.key} style={{ fontSize:12 }}>
                            {opt.label} <span style={{ color:'#aaa', fontSize:11 }}>({opt.score} pts)</span>
                        </Radio>
                    ))}
                </Radio.Group>
            ) : (
                <Checkbox.Group
                    value={selected}
                    onChange={handleMultiChange}
                    disabled={disabled}
                    style={{ display:'flex', flexDirection:'column', gap:6, marginLeft:8 }}>
                    {question.options.map(opt => (
                        <Checkbox key={opt.key} value={opt.key} style={{ fontSize:12 }}>
                            {opt.label} <span style={{ color:'#aaa', fontSize:11 }}>({opt.score} pts)</span>
                        </Checkbox>
                    ))}
                </Checkbox.Group>
            )}
        </div>
    );
}

// ─── Main page ──────────────────────────────────────────────────────────────────
export default function QoCIpcafAssessment() {
    const [facilities, setFacilities]     = useState([]);
    const [facilityId, setFacilityId]     = useState(null);
    const [assessmentDate, setAssessmentDate] = useState(moment());
    const [sections, setSections]         = useState([]);
    const [questions, setQuestions]       = useState([]);      // all 84, flat
    const [responses, setResponses]       = useState({});      // { [question_id]: { selected_options, score, comment } }
    const [activeSection, setActiveSection] = useState(null);
    const [assessmentId, setAssessmentId] = useState(null);
    const [status, setStatus]             = useState('draft');
    const [loading, setLoading]           = useState(false);
    const [saving, setSaving]             = useState(false);

    const [pastAssessments, setPastAssessments] = useState([]);
    const [pastLoading, setPastLoading]   = useState(false);
    const [viewOnly, setViewOnly]         = useState(false);

    // ── Offline lock + multi-user session state ────────────────────────────
    const [lockStatus, setLockStatus] = useState(null); // { sessions:[], offline_locked_by, offline_locked_by_name, ... }
    const [exporting, setExporting]   = useState(false);
    const [importingExcel, setImportingExcel] = useState(false);
    const importInputRef = useRef(null);
    const heartbeatRef    = useRef(null);
    const currentUserId   = parseInt(util.getUserId?.() || '0');
    const isAdminUser     = util.getModules?.()?.qoc_admin === 1 ||
                             parseInt(localStorage.getItem('is_admin') || '0') === 1;
    const isLockedByOther = lockStatus?.offline_locked_by &&
                             parseInt(lockStatus.offline_locked_by) !== currentUserId;
    const effectiveViewOnly = viewOnly || !!isLockedByOther;

    const canAssess  = util.canAdd('qoc_assess') || util.canEdit('qoc_assess');

    // ── Load facilities + reference data (sections + all questions) once ──────
    useEffect(() => {
        FacilityService.all({ status:1 }).then(r => setFacilities(r.data?.result?.data || []));
        Promise.all([
            QoCService.getIpcafSections(),
            QoCService.getIpcafQuestions(),
        ]).then(([secRes, qRes]) => {
            const secs = secRes.data?.data || [];
            const qs   = qRes.data?.data || [];
            setSections(secs);
            setQuestions(qs);
            if (secs.length) setActiveSection(secs[0].code);
        }).catch(() => message.error('Failed to load IPCAF question bank'));
    }, []);

    // ── When a facility is picked, show its past assessments ──────────────────
    useEffect(() => {
        if (!facilityId) { setPastAssessments([]); return; }
        loadPastAssessments();
    }, [facilityId]);

    // ── Heartbeat while an assessment is open for editing ──────────────────────
    useEffect(() => {
        if (!assessmentId || viewOnly) { stopHeartbeat(); return; }
        fetchLockStatus(assessmentId);
        startHeartbeat(assessmentId);
        return () => {
            stopHeartbeat();
            QoCService.clearIpcafOnlineEditing(assessmentId).catch(() => {});
        };
    }, [assessmentId, viewOnly]);

    const loadPastAssessments = async () => {
        setPastLoading(true);
        try {
            const res = await QoCService.getIpcafAssessments({ facility_id: facilityId });
            setPastAssessments(res.data?.data || []);
        } catch (e) { message.error('Failed to load past assessments'); }
        finally { setPastLoading(false); }
    };

    // ── Lock helpers ────────────────────────────────────────────────────────────
    const fetchLockStatus = async (aId) => {
        if (!aId) return;
        try {
            const res = await QoCService.getIpcafLockStatus(aId);
            setLockStatus(res.data?.data || null);
        } catch (e) { /* silent */ }
    };

    const startHeartbeat = (aId) => {
        const beat = () => {
            QoCService.heartbeatIpcafAssessment(aId).then(r => {
                setLockStatus(r.data?.data || null);
            }).catch(() => {});
        };
        beat();
        heartbeatRef.current = setInterval(beat, 30000);
    };

    const stopHeartbeat = () => {
        if (heartbeatRef.current) {
            clearInterval(heartbeatRef.current);
            heartbeatRef.current = null;
        }
    };

    const handleBreakLock = async () => {
        if (!assessmentId) return;
        try {
            await QoCService.clearIpcafOfflineLock(assessmentId);
            await fetchLockStatus(assessmentId);
            message.success('Offline lock cleared');
        } catch (e) { message.error('Failed to break lock'); }
    };

    // ── Section id -> code lookup, since questions carry section_id ───────────
    const sectionById = sections.reduce((acc, s) => { acc[s.id] = s; return acc; }, {});
    const questionsBySection = questions.reduce((acc, q) => {
        const code = sectionById[q.section_id]?.code;
        if (!code) return acc;
        (acc[code] = acc[code] || []).push(q);
        return acc;
    }, {});

    // ── Scoring (client-side mirror of the server calc, for live feedback) ────
    const sectionScores = sections.reduce((acc, s) => {
        const qs = questionsBySection[s.code] || [];
        acc[s.code] = qs.reduce((sum, q) => sum + (responses[q.id]?.score || 0), 0);
        return acc;
    }, {});
    const totalScore = Object.values(sectionScores).reduce((a, b) => a + b, 0);
    const currentLevel = levelFor(totalScore);
    const answeredCount = Object.values(responses).filter(r => (r.selected_options || []).length > 0).length;
    const pctComplete = questions.length > 0 ? Math.round((answeredCount / questions.length) * 100) : 0;

    const selectedFacility = facilities.find(f => f.id === facilityId);

    // ── Start a brand-new assessment for the selected facility/date ───────────
    const handleStartNew = async () => {
        if (!facilityId) { message.warning('Select a facility first'); return; }
        setLoading(true);
        try {
            const res = await QoCService.saveIpcafAssessment({
                facility_id: facilityId,
                assessment_date: assessmentDate.format('YYYY-MM-DD'),
                status: 'draft',
            });
            setAssessmentId(res.data?.data?.id);
            setResponses({});
            setStatus('draft');
            setViewOnly(false);
            message.success('New IPCAF assessment started');
            loadPastAssessments();
        } catch (e) {
            message.error('Failed to start assessment: ' + (e.response?.data?.message || e.message));
        } finally { setLoading(false); }
    };

    // ── Resume / view an existing assessment ───────────────────────────────────
    const handleOpenAssessment = async (asmt, readOnly) => {
        setLoading(true);
        try {
            const res = await QoCService.getIpcafAssessmentResponses(asmt.id);
            const rows = res.data?.data || [];
            const map = {};
            rows.forEach(r => {
                map[r.question_id] = {
                    selected_options: r.selected_options || [],
                    score: Number(r.score) || 0,
                    comment: r.comment || '',
                };
            });
            setResponses(map);
            setAssessmentId(asmt.id);
            setAssessmentDate(moment(asmt.assessment_date));
            setStatus(asmt.status);
            setViewOnly(!!readOnly);
            if (sections.length) setActiveSection(sections[0].code);
        } catch (e) {
            message.error('Failed to load assessment responses');
        } finally { setLoading(false); }
    };

    // ── Response change handler ────────────────────────────────────────────────
    const handleResponseChange = (questionId, selectedOptions, score) => {
        setResponses(prev => ({
            ...prev,
            [questionId]: { ...(prev[questionId] || {}), selected_options: selectedOptions, score },
        }));
    };

    // ── Save (draft or submit) ─────────────────────────────────────────────────
    const buildResponsesPayload = () => Object.entries(responses).map(([qid, r]) => ({
        question_id: Number(qid),
        selected_options: r.selected_options || [],
        comment: r.comment || '',
    }));

    const handleSave = async (submit = false) => {
        if (!assessmentId) { message.warning('Start an assessment first'); return; }
        if (submit && pctComplete < 100) {
            message.warning(`Cannot submit — ${questions.length - answeredCount} question(s) still unanswered.`);
            return;
        }
        setSaving(true);
        try {
            await QoCService.saveIpcafResponses(assessmentId, buildResponsesPayload());
            if (submit) {
                await QoCService.submitIpcafAssessment(assessmentId);
                setStatus('submitted');
                message.success('IPCAF assessment submitted for review ✓');
            } else {
                message.success('Draft saved ✓');
            }
            loadPastAssessments();
        } catch (e) {
            message.error('Save failed: ' + (e.response?.data?.message || e.message));
        } finally { setSaving(false); }
    };

    // ── Offline Excel export ────────────────────────────────────────────────────
    const handleExcelExport = async () => {
        if (!assessmentId) return;
        setExporting(true);
        try {
            const filename = await exportIpcafAssessmentExcel({
                facility: { id: facilityId, business_name: selectedFacility?.business_name },
                assessment: { id: assessmentId },
                sections, questions, responses,
                assessmentDate: assessmentDate.format('YYYY-MM-DD'),
            });
            await QoCService.setIpcafOfflineLock(assessmentId, { filename });
            await fetchLockStatus(assessmentId);
            message.success('Excel downloaded — assessment locked for offline work. Import back when done.');
        } catch (e) {
            message.error('Export failed: ' + e.message);
        } finally { setExporting(false); }
    };

    // ── Offline Excel import ────────────────────────────────────────────────────
    const handleExcelImport = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        e.target.value = '';
        setImportingExcel(true);
        try {
            const { responses: imported, meta, errors } = await importIpcafAssessmentExcel(file, questions);

            if (meta.ipcaf_assessment_id && String(meta.ipcaf_assessment_id) !== String(assessmentId)) {
                message.warning(
                    `This file was exported for assessment #${meta.ipcaf_assessment_id}, ` +
                    `but you currently have #${assessmentId} open. Imported anyway — double-check this is the right file.`
                );
            }

            if (errors.length > 0) {
                Modal.warning({
                    title: `Imported with ${errors.length} validation issue(s)`,
                    width: 520,
                    content: (
                        <div style={{ maxHeight:300, overflowY:'auto' }}>
                            {errors.map((err, i) => (
                                <div key={i} style={{ color:'#f5222d', fontSize:12, marginBottom:4 }}>⚠ {err}</div>
                            ))}
                            <div style={{ marginTop:12, color:'#555' }}>
                                Valid rows have been imported. Fix the above in the file and re-import to correct them.
                            </div>
                        </div>
                    ),
                });
            }

            const merged = { ...responses, ...imported };
            setResponses(merged);
            const answeredInFile = Object.keys(imported).length;
            message.success(`Imported ${answeredInFile} response(s) from Excel ✓`);

            if (assessmentId) {
                QoCService.clearIpcafOfflineLock(assessmentId).then(() => fetchLockStatus(assessmentId)).catch(() => {});
            }
        } catch (err) {
            message.error('Import failed: ' + err.message);
        } finally { setImportingExcel(false); }
    };

    const pastColumns = [
        { title:'Date', dataIndex:'assessment_date', width:110,
            render:v => moment(v).format('DD MMM YYYY') },
        { title:'Status', dataIndex:'status', width:130,
            render:v => <Tag color={v==='submitted' ? 'success' : 'default'}>{v}</Tag> },
        { title:'Total Score', dataIndex:'total_score', width:110,
            render:v => v != null ? `${parseFloat(v).toFixed(1)} / 800` : '—' },
        { title:'IPC Level', dataIndex:'ipc_level', width:120,
            render:v => v ? <Tag color={LEVEL_CFG[v]?.color}>{v}</Tag> : '—' },
        { title:'Offline', width:110, render:(_, row) => row.offline_locked_by
            ? <Tag color="purple">🔒 {row.offline_locked_by_name || 'locked'}</Tag> : null },
        { title:'', width:140, render:(_, row) => (
            <div style={{ display:'flex', gap:6 }}>
                <Button size="small" type="primary" onClick={() => handleOpenAssessment(row, false)}>
                    {row.status === 'draft' ? 'Continue' : 'Edit'}
                </Button>
                <Button size="small" icon={<EyeOutlined/>} onClick={() => handleOpenAssessment(row, true)}/>
            </div>
        )},
    ];

    const activeQuestions = questionsBySection[activeSection] || [];
    // Group consecutive questions sharing the same group_label under one subheading
    const groupedActiveQuestions = [];
    let lastGroup = null;
    activeQuestions.forEach(q => {
        if (q.group_label && q.group_label !== lastGroup) {
            groupedActiveQuestions.push({ __groupHeader: q.group_label });
            lastGroup = q.group_label;
        } else if (!q.group_label) {
            lastGroup = null;
        }
        groupedActiveQuestions.push(q);
    });

    return (
        <div className="page-content">
            <div className="page-head uc d-flex">
                <div className="my-auto">
                    <h2>IPCAF Assessment</h2>
                    <div style={{ fontSize:12, color:'#888', marginTop:2 }}>
                        WHO Infection Prevention &amp; Control Assessment Framework
                    </div>
                </div>
            </div>

            <div className="page-pad">
                <Card size="small" style={{ marginBottom:16 }}>
                    <Row gutter={12} align="middle">
                        <Col span={8}>
                            <Select showSearch optionFilterProp="children" allowClear
                                placeholder="Select facility" style={{ width:'100%' }}
                                value={facilityId}
                                onChange={v => { setFacilityId(v); setAssessmentId(null); setResponses({}); }}>
                                {facilities.map(f => (
                                    <Select.Option key={f.id} value={f.id}>{f.business_name}</Select.Option>
                                ))}
                            </Select>
                        </Col>
                        <Col span={5}>
                            <DatePicker style={{ width:'100%' }} value={assessmentDate}
                                onChange={v => setAssessmentDate(v || moment())}/>
                        </Col>
                        <Col span={5}>
                            {canAssess && (
                                <Button icon={<PlusOutlined/>} type="primary" loading={loading}
                                    disabled={!facilityId}
                                    onClick={handleStartNew}>
                                    Start New Assessment
                                </Button>
                            )}
                        </Col>
                    </Row>
                </Card>

                {facilityId && (
                    <Card size="small" title="Past IPCAF Assessments" style={{ marginBottom:16 }} loading={pastLoading}>
                        {pastAssessments.length === 0
                            ? <Alert type="info" showIcon message="No IPCAF assessments yet for this facility."/>
                            : <Table size="small" dataSource={pastAssessments} columns={pastColumns} rowKey="id" pagination={false}/>
                        }
                    </Card>
                )}

                {!!assessmentId && (
                    <Row gutter={16}>
                        <Col span={18}>
                            <Card size="small" bodyStyle={{ padding:12 }}>

                                {/* ── Offline lock warnings ── */}
                                {lockStatus?.offline_locked_by && lockStatus.offline_locked_by !== currentUserId && (
                                    <Alert type="error" showIcon style={{ marginBottom:10 }}
                                        message={<span style={{ fontWeight:700 }}>🔒 Locked for offline work</span>}
                                        description={
                                            <div>
                                                <strong>{lockStatus.offline_locked_by_name}</strong> exported this assessment
                                                to Excel and is working offline. Do not capture responses online until
                                                they import the file back.
                                                {isAdminUser && (
                                                    <Button danger size="small" style={{ marginLeft:12 }}
                                                        onClick={handleBreakLock}>
                                                        Break Lock (Admin)
                                                    </Button>
                                                )}
                                            </div>
                                        }/>
                                )}
                                {lockStatus?.offline_locked_by && lockStatus.offline_locked_by === currentUserId && (
                                    <Alert type="warning" showIcon style={{ marginBottom:10 }}
                                        message={<span style={{ fontWeight:700 }}>📥 You have this file open offline</span>}
                                        description={
                                            <div>
                                                You exported this assessment to Excel. Import the file back when done,
                                                or clear the lock to continue online instead.
                                                <Button size="small" style={{ marginLeft:12 }} onClick={handleBreakLock}>
                                                    Clear Lock &amp; Work Online
                                                </Button>
                                            </div>
                                        }/>
                                )}
                                {!lockStatus?.offline_locked_by && (lockStatus?.sessions || [])
                                    .filter(s => parseInt(s.user_id) !== currentUserId).length > 0 && (
                                    <Alert type="warning" showIcon style={{ marginBottom:10 }}
                                        message={<span style={{ fontWeight:700 }}>
                                            👁 {lockStatus.sessions.filter(s => parseInt(s.user_id) !== currentUserId).length}
                                            {' '}other{lockStatus.sessions.filter(s => parseInt(s.user_id) !== currentUserId).length === 1 ? '' : 's'} also editing this online
                                        </span>}
                                        description="Responses may conflict — coordinate before submitting."/>
                                )}

                                {loading ? <div style={{ textAlign:'center', padding:60 }}><Spin size="large"/></div> : (
                                <>
                                    {/* ── Excel offline workflow buttons ── */}
                                    {!viewOnly && (
                                        <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginBottom:10 }}>
                                            <Tooltip title="Download this assessment as Excel for offline completion">
                                                <Button size="small" icon={<FileExcelOutlined/>} loading={exporting}
                                                    onClick={handleExcelExport}
                                                    style={{ borderColor:'#52c41a', color:'#52c41a' }}>
                                                    Export Excel
                                                </Button>
                                            </Tooltip>
                                            <Tooltip title="Upload a completed Excel file to import responses">
                                                <Button size="small"
                                                    icon={importingExcel ? <LoadingOutlined/> : <UploadOutlined/>}
                                                    loading={importingExcel}
                                                    onClick={() => importInputRef.current?.click()}
                                                    style={{ borderColor:'#1890ff', color:'#1890ff' }}>
                                                    Import Excel
                                                </Button>
                                            </Tooltip>
                                            <input ref={importInputRef} type="file" accept=".xlsx"
                                                style={{ display:'none' }} onChange={handleExcelImport}/>
                                        </div>
                                    )}

                                    <Tabs activeKey={activeSection} onChange={setActiveSection} type="card" size="small">
                                        {sections.map(sec => (
                                            <TabPane
                                                key={sec.code}
                                                tab={
                                                    <span>
                                                        <span style={{
                                                            display:'inline-block', width:8, height:8, borderRadius:'50%',
                                                            background: SECTION_COLORS[sec.code], marginRight:6
                                                        }}/>
                                                        {sec.code} <Tag style={{ marginLeft:4 }}>
                                                            {(sectionScores[sec.code] || 0).toFixed(1)}/100
                                                        </Tag>
                                                    </span>
                                                }>
                                                <div style={{ fontSize:13, fontWeight:700, color:SECTION_COLORS[sec.code], marginBottom:10 }}>
                                                    {sec.title}
                                                </div>
                                                {groupedActiveQuestions.map((item, i) => (
                                                    item.__groupHeader
                                                        ? <div key={`grp-${i}`} style={{
                                                            fontSize:11, fontWeight:700, color:'#888',
                                                            textTransform:'uppercase', margin:'14px 0 6px' }}>
                                                            {item.__groupHeader}
                                                        </div>
                                                        : <QuestionBlock
                                                            key={item.id}
                                                            question={item}
                                                            response={responses[item.id]}
                                                            onChange={handleResponseChange}
                                                            disabled={effectiveViewOnly}
                                                        />
                                                ))}
                                            </TabPane>
                                        ))}
                                    </Tabs>
                                </>
                                )}
                            </Card>
                        </Col>

                        <Col span={6}>
                            <Card size="small" style={{ marginBottom:12 }}>
                                <div style={{ fontSize:10, color:'#888', fontWeight:700, marginBottom:4 }}>COMPLETION</div>
                                <Progress percent={pctComplete} strokeColor="#1890ff"/>
                                <div style={{ fontSize:11, color:'#888', marginTop:4 }}>
                                    {answeredCount} / {questions.length} questions answered
                                </div>
                            </Card>

                            <Card size="small" style={{ marginBottom:12, background:`${LEVEL_CFG[currentLevel].color}08` }}>
                                <div style={{ fontSize:10, color:'#888', fontWeight:700, marginBottom:4 }}>TOTAL SCORE</div>
                                <div style={{ fontSize:28, fontWeight:900, color:LEVEL_CFG[currentLevel].color }}>
                                    {totalScore.toFixed(1)}
                                    <span style={{ fontSize:14, color:'#aaa' }}> / 800</span>
                                </div>
                                <Tag color={LEVEL_CFG[currentLevel].color} style={{ marginTop:4 }}>{currentLevel}</Tag>
                                <Divider style={{ margin:'10px 0' }}/>
                                {sections.map(sec => (
                                    <div key={sec.code} style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                                        <span style={{ fontSize:11, color:SECTION_COLORS[sec.code], fontWeight:600 }}>{sec.code}</span>
                                        <span style={{ fontSize:11, fontWeight:700 }}>
                                            {(sectionScores[sec.code] || 0).toFixed(1)}/100
                                        </span>
                                    </div>
                                ))}
                            </Card>

                            {!effectiveViewOnly && (
                                <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                                    <Button icon={<SaveOutlined/>} loading={saving} onClick={() => handleSave(false)}>
                                        Save Draft
                                    </Button>
                                    <Button type="primary" icon={<SendOutlined/>} loading={saving}
                                        disabled={pctComplete < 100}
                                        onClick={() => handleSave(true)}>
                                        Submit {pctComplete < 100 && `(${pctComplete}%)`}
                                    </Button>
                                </div>
                            )}
                        </Col>
                    </Row>
                )}
            </div>
        </div>
    );
}